import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import pokeBallImg from '../assets/images/poke_ball.webp';
import greatBallImg from '../assets/images/great_ball.webp';
import ultraBallImg from '../assets/images/ultra_ball.webp';
import masterBallImg from '../assets/images/master_ball.webp';
import colorlessImg from '../assets/images/colorless.png';
import darkImg from '../assets/images/dark.png';
import electricImg from '../assets/images/electric.png';
import fightingImg from '../assets/images/fighting.png';
import fireImg from '../assets/images/fire.png';
import grassImg from '../assets/images/grass.png';
import metalImg from '../assets/images/metal.png';
import waterImg from '../assets/images/water.png';
import psychicImg from '../assets/images/psychic.png';
import {useSelector} from 'react-redux';
import { useDeleteDeck } from '../api/cards';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const rankImages = {
  'Beginner': pokeBallImg,
  'Poké Ball': pokeBallImg,
  'Great Ball': greatBallImg,
  'Ultra Ball': ultraBallImg,
  'Master Ball': masterBallImg,
};

const typeImages = {
  colorless: colorlessImg,
  dark: darkImg,
  electric: electricImg,
  fighting: fightingImg,
  fire: fireImg,
  grass: grassImg,
  metal: metalImg,
  water: waterImg,
  psychic: psychicImg,
};

const DeckCard = ({ deck, profile = false }) => {
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteDeckMutation = useDeleteDeck();
  const queryClient = useQueryClient();

  // Get rank tier (e.g., 'Great Ball' from 'Great Ball - 1')
  const rankTier = deck.creator?.rank ? deck.creator.rank.split(' - ')[0] : undefined;
  const rankImg = rankTier ? (rankImages[rankTier] || pokeBallImg) : pokeBallImg;

  // Get deck type image
  const deckTypeImg = typeImages[deck.deck_type?.toLowerCase()] || colorlessImg;

  const handleDeleteDeck = () => {
    deleteDeckMutation.mutate(deck.id, {
      onSuccess: () => {
        toast.success('Deck deleted successfully');
        setIsDeleteModalOpen(false);
        // Invalidate and refetch decks query
        if(profile) {
          queryClient.invalidateQueries({ queryKey: ['userDecks'] });
        } else {
          queryClient.invalidateQueries({ queryKey: ['decks'] });
        }
      },
      onError: (error) => {
        toast.error('Failed to delete deck');
        console.error('Failed to delete deck:', error);
      }
    });
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        whileHover={{ scale: 1.02, y: -5 }}
        transition={{ duration: 0.2 }}
        className="relative overflow-hidden rounded-2xl backdrop-blur-xl bg-gradient-to-br from-gray-900/90 to-gray-800/90 border border-purple-500/20 shadow-lg hover:shadow-purple-500/20 transition-all duration-300"
      >
        {/* Glow Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-blue-500 opacity-10 blur"></div>

        {/* Card Content */}
        <div className="relative p-6 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              {deck.name}
            </h3>
            <div className="flex items-center gap-2">
              <img src={deckTypeImg} alt={deck.deck_type} className="w-10 h-10 drop-shadow-md" />
              <span className="text-base font-semibold text-purple-200 capitalize drop-shadow">{deck.deck_type}</span>
            </div>
          </div>

          {/* Card Preview */}
          <div className="relative h-32 mb-6">
            {deck.cards.slice(0, 4).map((card, index) => (
              <div
                key={card.card_details.card_id}
                className="absolute w-24 h-32 rounded-lg overflow-hidden shadow-lg transition-all duration-200"
                style={{
                  left: `${index * 20}%`,
                  transform: `rotate(${(index - 1) * 5}deg)`,
                  zIndex: 3 - index
                }}
              >
                <img
                  src={card.card_details.image_url}
                  alt={card.card_details.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          {/* User Rank at bottom right, with ball image */}
          {deck.creator?.rank && (
            <div className="absolute bottom-24 max-md:bottom-4 right-4 z-10 flex items-center gap-2 pointer-events-none select-none">
              <img src={rankImg} alt={rankTier + ' Ball'} className="w-24 h-24 opacity-80 drop-shadow-lg" />
            </div>
          )}

          <div className="flex flex-row max-md:flex-col items-center max-md:items-start justify-between pt-4 border-t border-purple-500/20 mt-auto gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Link
                to={`/profile/${deck.creator?.username || deck.creator?.id || ''}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-purple-900/80 to-blue-900/80 border border-purple-500/30 shadow hover:shadow-purple-500/30 transition-all duration-200"
                style={{ minWidth: 0 }}
              >
                <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a6 6 0 016 6c0 2.21-1.2 4.15-3 5.19V17a1 1 0 01-2 0v-3.81C5.2 12.15 4 10.21 4 8a6 6 0 016-6zm0 2a4 4 0 00-4 4c0 1.306.835 2.417 2 2.83V15h4v-6.17c1.165-.413 2-1.524 2-2.83a4 4 0 00-4-4z"/></svg>
                <span className="text-sm font-semibold text-white truncate">Creator {deck.creator?.username || 'Anonymous'}</span>
              </Link>
              {deck?.creator?.username === loggedInUser?.username && (
                <div 
                  onClick={() => setIsDeleteModalOpen(true)}
                  className='p-2 bg-red-400 rounded-full cursor-pointer hover:bg-red-600 transition-all duration-300'
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate(`/deck/${deck.id}`)}
              className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium 
                       hover:from-purple-500 hover:to-blue-500 transform hover:scale-105 transition-all duration-200
                       focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 focus:outline-none"
            >
              View Deck
            </button>
          </div>
        </div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4 border border-purple-500/20">
            <h3 className="text-xl font-bold text-white mb-4">Delete Deck</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete "{deck.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDeck}
                disabled={deleteDeckMutation.isPending}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteDeckMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeckCard; 